// Avatar — colourways picked deterministically from name hash.
// 8 colorways (av-1..av-8) defined w globals.css.

import type { ReactNode } from 'react';

type Size = 'sm' | 'md' | 'lg' | 'xl';

function classFromName(name: string): string {
  if (!name) return 'av-1';
  const sum = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return `av-${(sum % 8) + 1}`;
}

function initialsFromName(name: string, maxChars: number = 2): string {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, maxChars).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export function Avatar({
  name,
  initials,
  size = 'md',
  square = false,
  colorClass,
  imageUrl,
}: {
  name?: string;
  initials?: string;
  size?: Size;
  square?: boolean;
  colorClass?: string;
  imageUrl?: string | null;  // Google profile picture URL z OAuth
}) {
  const text = initials ?? initialsFromName(name ?? '');
  const cls = colorClass ?? classFromName(name ?? initials ?? '');
  const sizeCls = square
    ? (size === 'lg' || size === 'xl' ? 'logo-tile-lg' : '')
    : (size === 'sm' ? 'avatar-sm' : size === 'lg' ? 'avatar-lg' : size === 'xl' ? 'avatar-xl' : '');

  const containerClass = [square ? 'logo-tile' : 'avatar', cls, sizeCls]
    .filter(Boolean)
    .join(' ');

  // Gdy mamy URL do zdjęcia profilowego (Google OAuth), pokaż foto.
  // Fallback do inicjałów jeśli load fails (alt + background z initialsami).
  if (imageUrl) {
    return (
      <span className={containerClass}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageUrl}
          alt={text}
          width={36}
          height={36}
          referrerPolicy="no-referrer"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 'inherit',
          }}
        />
      </span>
    );
  }

  return <span className={containerClass}>{text}</span>;
}

export { classFromName as avatarClassFromName, initialsFromName };
